import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatPercentage, formatDate } from '../utils/helpers';
import { 
  Plus, Target, CheckCircle2, Clock, User, Trash2, Edit, Eye,
  Link2, ChevronRight, Calendar, Flag, ListTodo, BarChart3,
  Play, Pause, RotateCcw, X, Save, AlertCircle, TrendingUp, Layers, GripVertical,
  Loader2, RefreshCw, Megaphone
} from 'lucide-react';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';

// Custom modifier to position drag overlay directly under cursor
const snapToCursor = ({ transform, activatorEvent }) => {
  // Position the overlay so the top-left corner is near the cursor
  return {
    x: transform.x,
    y: transform.y,
  };
};

// Simple Draggable Task Card
const DraggableTask = ({ task, goals, employees, priorityConfig, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task.id,
    data: { task }
  });

  const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || 'Niet toegewezen';

  // Hide the original element completely when dragging - DragOverlay shows the preview
  if (isDragging) {
    return (
      <div 
        ref={setNodeRef} 
        className="p-3 bg-stone-100 rounded-lg border-2 border-dashed border-stone-300 opacity-40"
      >
        <div className="flex items-center gap-2 text-stone-400">
          <GripVertical className="w-4 h-4" />
          <span className="text-sm">Wordt verplaatst...</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className="p-3 bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-2">
        <div 
          {...listeners} 
          {...attributes}
          className="mt-1 text-stone-400 hover:text-[#C2410C] cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => onEdit(task)}
        >
          <p className={`font-medium text-sm ${task.status === 'voltooid' ? 'line-through text-[#57534E]' : ''}`}>
            {task.title}
          </p>
          <div className="flex items-center gap-2 mt-1 text-xs text-[#57534E] flex-wrap">
            {task.goal_id && (
              <span className="flex items-center gap-1 bg-stone-100 px-1.5 py-0.5 rounded">
                <Target className="w-3 h-3" />
                {goals.find(g => g.id === task.goal_id)?.title?.substring(0, 12)}...
              </span>
            )}
            {task.assigned_to && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {getEmployeeName(task.assigned_to).split(' ')[0]}
              </span>
            )}
          </div>
        </div>
        <Badge className={`${priorityConfig[task.priority]?.bg} ${priorityConfig[task.priority]?.color} text-[10px] shrink-0`}>
          {task.priority}
        </Badge>
      </div>
    </div>
  );
};

// Droppable Column
const DroppableColumn = ({ id, title, tasks, color, goals, employees, priorityConfig, onEdit, isOver }) => {
  const { setNodeRef } = useDroppable({ id });

  return (
    <Card className={`card-base h-full transition-all ${isOver ? 'ring-2 ring-[#C2410C] bg-[#C2410C]/5' : ''}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${color}`} />
          {title} ({tasks.length})
          {isOver && <span className="text-xs text-[#C2410C] ml-auto animate-pulse">Loslaten om te plaatsen</span>}
        </CardTitle>
      </CardHeader>
      <CardContent 
        ref={setNodeRef}
        className="space-y-2 min-h-[250px] max-h-[500px] overflow-y-auto"
      >
        {tasks.map(task => (
          <DraggableTask
            key={task.id}
            task={task}
            goals={goals}
            employees={employees}
            priorityConfig={priorityConfig}
            onEdit={onEdit}
          />
        ))}
        {tasks.length === 0 && (
          <div className={`text-sm text-center py-8 border-2 border-dashed rounded-lg transition-colors ${
            isOver ? 'border-[#C2410C] bg-[#C2410C]/10 text-[#C2410C]' : 'border-stone-200 text-[#57534E]'
          }`}>
            {isOver ? '⬇️ Loslaten om hier te plaatsen' : 'Sleep taken hierheen'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Droppable Goal Card
const DroppableGoalTarget = ({ goal, tasks, isOver, onEdit }) => {
  const { setNodeRef } = useDroppable({ id: `goal-${goal.id}` });
  
  const goalTasks = tasks.filter(t => t.goal_id === goal.id);
  const progress = goal.progress || 0;

  return (
    <div 
      ref={setNodeRef}
      className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
        isOver 
          ? 'border-[#C2410C] bg-[#C2410C]/10 scale-[1.02]' 
          : 'border-stone-200 bg-white hover:border-stone-300'
      }`}
      onClick={() => onEdit(goal)}
    >
      <div className="flex items-center gap-3">
        <div className="relative w-10 h-10 shrink-0">
          <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#E7E5E4" strokeWidth="3" />
            <circle 
              cx="18" cy="18" r="15.9" fill="none" 
              stroke={isOver ? '#C2410C' : '#059669'} 
              strokeWidth="3"
              strokeDasharray={`${progress} 100`}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{goal.title}</p>
          <p className="text-xs text-[#57534E]">
            {goalTasks.length} taken • {goal.period}
          </p>
        </div>
        {isOver && (
          <div className="text-[#C2410C] text-xs font-medium animate-pulse shrink-0">
            + Koppelen
          </div>
        )}
      </div>
    </div>
  );
};

// Droppable Unassign Zone
const DroppableUnassignZone = ({ isOver, hasGoal }) => {
  const { setNodeRef } = useDroppable({ id: 'unassign' });

  return (
    <div 
      ref={setNodeRef}
      className={`p-3 rounded-lg border-2 border-dashed transition-all ${
        isOver && hasGoal
          ? 'border-rose-400 bg-rose-100'
          : hasGoal 
            ? 'border-rose-300 bg-rose-50'
            : 'border-stone-200 bg-stone-50'
      }`}
    >
      <p className={`text-xs text-center ${isOver && hasGoal ? 'text-rose-600 font-medium' : 'text-[#57534E]'}`}>
        {isOver && hasGoal 
          ? '📤 Loslaten om te ontkoppelen'
          : '📤 Sleep hier om van doel te ontkoppelen'}
      </p>
    </div>
  );
};

const DoelenStructuurPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [kpiTargets, setKpiTargets] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [activeDropId, setActiveDropId] = useState(null);
  const [syncingKPIs, setSyncingKPIs] = useState(false);
  const [syncingMarketing, setSyncingMarketing] = useState(false);
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  
  // Dialogs
  const [goalDialog, setGoalDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Selected items
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('overzicht');
  const [filterPeriod, setFilterPeriod] = useState('alle');
  const [filterStatus, setFilterStatus] = useState('alle');

  // Forms
  const [goalForm, setGoalForm] = useState({
    title: '',
    description: '',
    period: 'maand',
    assigned_to: '',
    due_date: '',
    category: '',
    target_value: '',
    target_unit: ''
  });

  const [taskForm, setTaskForm] = useState({
    goal_id: '',
    title: '',
    description: '',
    assigned_to: '',
    priority: 'normaal',
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, tasksRes, empRes, kpiRes] = await Promise.all([
        api.get('/goals'),
        api.get('/tasks'),
        api.get('/employees'),
        api.get('/kpi-targets', { params: { year: new Date().getFullYear() } })
      ]);
      setGoals(goalsRes.data);
      setTasks(tasksRes.data);
      setEmployees(empRes.data);
      setKpiTargets(kpiRes.data);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  // Sync functions
  const handleSyncKPIs = async () => {
    setSyncingKPIs(true);
    try {
      const res = await api.post('/kpi-targets/sync-goals');
      toast.success(res.data.message || 'KPI doelen gesynchroniseerd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij synchroniseren KPIs');
    } finally {
      setSyncingKPIs(false);
    }
  };

  const handleSyncMarketing = async () => {
    setSyncingMarketing(true);
    try {
      const currentYear = new Date().getFullYear();
      const res = await api.post(`/marketing-plan/${currentYear}/sync-goals`);
      if (res.data.success) {
        toast.success(res.data.message || 'Marketing doelen gesynchroniseerd');
      } else {
        toast.error(res.data.message || 'Geen marketing plan gevonden');
      }
      fetchData();
    } catch (error) {
      toast.error('Fout bij synchroniseren marketing doelen');
    } finally {
      setSyncingMarketing(false);
    }
  };

  // Reset forms
  const resetGoalForm = () => {
    setGoalForm({
      title: '', description: '', period: 'maand', assigned_to: '',
      due_date: '', category: '', target_value: '', target_unit: ''
    });
    setEditMode(false);
    setSelectedGoal(null);
  };

  const resetTaskForm = () => {
    setTaskForm({
      goal_id: '', title: '', description: '', assigned_to: '',
      priority: 'normaal', due_date: ''
    });
    setEditMode(false);
    setSelectedTask(null);
  };

  // Goal CRUD
  const handleAddGoal = async (e) => {
    e.preventDefault();
    try {
      await api.post('/goals', {
        ...goalForm,
        assigned_to: goalForm.assigned_to || null,
        progress: 0,
        status: 'niet_gestart'
      });
      toast.success('Doel toegevoegd');
      setGoalDialog(false);
      resetGoalForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleUpdateGoal = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    try {
      await api.put(`/goals/${selectedGoal.id}`, {
        ...goalForm,
        assigned_to: goalForm.assigned_to || null
      });
      toast.success('Doel bijgewerkt');
      setGoalDialog(false);
      setDetailDialog(false);
      resetGoalForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDeleteGoal = async (id) => {
    if (!window.confirm('Doel verwijderen? Alle gekoppelde taken blijven bestaan.')) return;
    try {
      await api.delete(`/goals/${id}`);
      toast.success('Doel verwijderd');
      setDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleUpdateGoalStatus = async (goalId, status) => {
    try {
      const progress = status === 'voltooid' ? 100 : status === 'bezig' ? 50 : 0;
      await api.put(`/goals/${goalId}/progress`, null, {
        params: { progress, status }
      });
      toast.success('Status bijgewerkt');
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  // Task CRUD
  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tasks', {
        ...taskForm,
        goal_id: taskForm.goal_id || null,
        assigned_to: taskForm.assigned_to || null
      });
      toast.success('Taak toegevoegd');
      setTaskDialog(false);
      resetTaskForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    try {
      await api.put(`/tasks/${selectedTask.id}`, {
        ...taskForm,
        goal_id: taskForm.goal_id || null,
        assigned_to: taskForm.assigned_to || null
      });
      toast.success('Taak bijgewerkt');
      setTaskDialog(false);
      resetTaskForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm('Taak verwijderen?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success('Taak verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleUpdateTaskStatus = async (taskId, status) => {
    try {
      await api.put(`/tasks/${taskId}/status`, null, { params: { status } });
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  // Assign task to goal via drag & drop
  const handleAssignTaskToGoal = async (taskId, goalId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      
      await api.put(`/tasks/${taskId}`, {
        ...task,
        goal_id: goalId
      });
      
      const goal = goals.find(g => g.id === goalId);
      toast.success(`Taak gekoppeld aan "${goal?.title}"`);
      fetchData();
    } catch (error) {
      toast.error('Fout bij koppelen');
    }
  };

  // Unassign task from goal
  const handleUnassignTask = async (taskId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      
      await api.put(`/tasks/${taskId}`, {
        ...task,
        goal_id: null
      });
      
      toast.success('Taak ontkoppeld van doel');
      fetchData();
    } catch (error) {
      toast.error('Fout bij ontkoppelen');
    }
  };

  // Open goal detail
  const openGoalDetail = (goal) => {
    setSelectedGoal(goal);
    setDetailDialog(true);
  };

  // Open goal edit
  const openGoalEdit = (goal) => {
    setSelectedGoal(goal);
    setGoalForm({
      title: goal.title || '',
      description: goal.description || '',
      period: goal.period || 'maand',
      assigned_to: goal.assigned_to || '',
      due_date: goal.due_date || '',
      category: goal.category || '',
      target_value: goal.target_value || '',
      target_unit: goal.target_unit || ''
    });
    setEditMode(true);
    setGoalDialog(true);
  };

  // Open task edit
  const openTaskEdit = (task) => {
    setSelectedTask(task);
    setTaskForm({
      goal_id: task.goal_id || '',
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      priority: task.priority || 'normaal',
      due_date: task.due_date || ''
    });
    setEditMode(true);
    setTaskDialog(true);
  };

  // Helpers
  const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || 'Niet toegewezen';
  
  const getLinkedKPI = (goal) => {
    if (goal.kpi_target_id) {
      return kpiTargets.find(k => k.id === goal.kpi_target_id);
    }
    if (goal.category === 'kpi' || goal.title?.toLowerCase().includes('kpi:')) {
      return kpiTargets.find(k => goal.title?.includes(k.name));
    }
    return null;
  };

  // Filter goals
  const filteredGoals = goals.filter(g => {
    if (filterPeriod !== 'alle' && g.period !== filterPeriod) return false;
    if (filterStatus !== 'alle' && g.status !== filterStatus) return false;
    return true;
  });

  // Stats
  const totalGoals = goals.length;
  const completedGoals = goals.filter(g => g.status === 'voltooid').length;
  const inProgressGoals = goals.filter(g => g.status === 'bezig').length;
  const notStartedGoals = goals.filter(g => g.status === 'niet_gestart').length;
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'voltooid').length;
  const kpiGoals = goals.filter(g => g.category === 'kpi' || g.kpi_target_id).length;

  const statusColors = {
    niet_gestart: { bg: 'bg-stone-100', text: 'text-stone-700', color: '#78716C' },
    bezig: { bg: 'bg-amber-100', text: 'text-amber-700', color: '#D97706' },
    voltooid: { bg: 'bg-emerald-100', text: 'text-emerald-700', color: '#059669' }
  };

  const statusLabels = {
    niet_gestart: 'Niet gestart',
    bezig: 'Bezig',
    voltooid: 'Voltooid'
  };

  const priorityConfig = {
    laag: { color: 'text-sky-600', bg: 'bg-sky-100' },
    normaal: { color: 'text-amber-600', bg: 'bg-amber-100' },
    hoog: { color: 'text-rose-600', bg: 'bg-rose-100' }
  };

  const periodLabels = {
    week: 'Week',
    maand: 'Maand',
    kwartaal: 'Kwartaal',
    jaar: 'Jaar'
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="doelen-structuur-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Doelen & Structuur
          </h1>
          <p className="text-[#57534E] mt-1">
            Beheer doelen, taken en volg voortgang
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            className="text-purple-600 border-purple-400 hover:bg-purple-50"
            onClick={handleSyncKPIs}
            disabled={syncingKPIs}
          >
            {syncingKPIs ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" />Sync KPIs</>
            )}
          </Button>
          <Button 
            variant="outline" 
            className="text-emerald-600 border-emerald-400 hover:bg-emerald-50"
            onClick={handleSyncMarketing}
            disabled={syncingMarketing}
          >
            {syncingMarketing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
            ) : (
              <><Megaphone className="w-4 h-4 mr-2" />Sync Marketing</>
            )}
          </Button>
          <Button 
            variant="outline" 
            className="text-[#C2410C] border-[#C2410C]"
            onClick={() => navigate('/company/kpi-management')}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            KPI Module
          </Button>
          <Dialog open={taskDialog} onOpenChange={(open) => { setTaskDialog(open); if (!open) resetTaskForm(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary">
                <Plus className="w-4 h-4 mr-2" />
                Taak
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editMode ? 'Taak bewerken' : 'Nieuwe taak'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={editMode ? handleUpdateTask : handleAddTask} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Titel *</Label>
                  <Input
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    placeholder="Wat moet er gebeuren?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gekoppeld doel</Label>
                  <Select
                    value={taskForm.goal_id || "none"}
                    onValueChange={(value) => setTaskForm({ ...taskForm, goal_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecteer doel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Geen doel</SelectItem>
                      {goals.map(goal => (
                        <SelectItem key={goal.id} value={goal.id}>{goal.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Toegewezen aan</Label>
                    <Select
                      value={taskForm.assigned_to || "none"}
                      onValueChange={(value) => setTaskForm({ ...taskForm, assigned_to: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Niemand</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Prioriteit</Label>
                    <Select
                      value={taskForm.priority}
                      onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="laag">🟢 Laag</SelectItem>
                        <SelectItem value="normaal">🟡 Normaal</SelectItem>
                        <SelectItem value="hoog">🔴 Hoog</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Deadline</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Beschrijving</Label>
                  <Textarea
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    placeholder="Extra details..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 btn-primary">
                    <Save className="w-4 h-4 mr-2" />
                    {editMode ? 'Opslaan' : 'Toevoegen'}
                  </Button>
                  {editMode && (
                    <Button type="button" variant="outline" onClick={() => { setTaskDialog(false); resetTaskForm(); }}>
                      Annuleren
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={goalDialog} onOpenChange={(open) => { setGoalDialog(open); if (!open) resetGoalForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-goal-btn">
                <Plus className="w-4 h-4 mr-2" />
                Doel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Doel bewerken' : 'Nieuw doel'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={editMode ? handleUpdateGoal : handleAddGoal} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Titel *</Label>
                  <Input
                    value={goalForm.title}
                    onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                    placeholder="bijv. Omzet verhogen met 10%"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Periode</Label>
                    <Select
                      value={goalForm.period}
                      onValueChange={(value) => setGoalForm({ ...goalForm, period: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="week">Week</SelectItem>
                        <SelectItem value="maand">Maand</SelectItem>
                        <SelectItem value="kwartaal">Kwartaal</SelectItem>
                        <SelectItem value="jaar">Jaar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Categorie</Label>
                    <Select
                      value={goalForm.category || "none"}
                      onValueChange={(value) => setGoalForm({ ...goalForm, category: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Geen</SelectItem>
                        <SelectItem value="omzet">💰 Omzet</SelectItem>
                        <SelectItem value="kpi">📊 KPI</SelectItem>
                        <SelectItem value="personeel">👥 Personeel</SelectItem>
                        <SelectItem value="kwaliteit">⭐ Kwaliteit</SelectItem>
                        <SelectItem value="marketing">📣 Marketing</SelectItem>
                        <SelectItem value="operationeel">⚙️ Operationeel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Deadline</Label>
                    <Input
                      type="date"
                      value={goalForm.due_date}
                      onChange={(e) => setGoalForm({ ...goalForm, due_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Verantwoordelijke</Label>
                    <Select
                      value={goalForm.assigned_to || "none"}
                      onValueChange={(value) => setGoalForm({ ...goalForm, assigned_to: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Niemand</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Beschrijving</Label>
                  <Textarea
                    value={goalForm.description}
                    onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                    placeholder="Wat wil je bereiken en waarom?"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 btn-primary">
                    <Save className="w-4 h-4 mr-2" />
                    {editMode ? 'Opslaan' : 'Toevoegen'}
                  </Button>
                  {editMode && (
                    <Button type="button" variant="outline" onClick={() => { setGoalDialog(false); resetGoalForm(); }}>
                      Annuleren
                    </Button>
                  )}
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100">
          <TabsTrigger value="overzicht" className="data-[state=active]:bg-white">
            <Layers className="w-4 h-4 mr-2" />
            Overzicht
          </TabsTrigger>
          <TabsTrigger value="doelen" className="data-[state=active]:bg-white">
            <Target className="w-4 h-4 mr-2" />
            Doelen ({goals.length})
          </TabsTrigger>
          <TabsTrigger value="taken" className="data-[state=active]:bg-white">
            <ListTodo className="w-4 h-4 mr-2" />
            Taken ({tasks.length})
          </TabsTrigger>
        </TabsList>

        {/* Overzicht Tab */}
        <TabsContent value="overzicht" className="mt-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="kpi-tile">
              <p className="kpi-label">Totaal Doelen</p>
              <p className="kpi-value">{totalGoals}</p>
            </div>
            <div className="kpi-tile">
              <p className="kpi-label">Voltooid</p>
              <p className="kpi-value text-emerald-600">{completedGoals}</p>
            </div>
            <div className="kpi-tile">
              <p className="kpi-label">Bezig</p>
              <p className="kpi-value text-amber-600">{inProgressGoals}</p>
            </div>
            <div className="kpi-tile">
              <p className="kpi-label">Niet Gestart</p>
              <p className="kpi-value text-stone-500">{notStartedGoals}</p>
            </div>
            <div className="kpi-tile">
              <p className="kpi-label">KPI Doelen</p>
              <p className="kpi-value text-[#C2410C]">{kpiGoals}</p>
            </div>
            <div className="kpi-tile">
              <p className="kpi-label">Taken Klaar</p>
              <p className="kpi-value">{completedTasks}/{totalTasks}</p>
            </div>
          </div>

          {/* Recent Goals */}
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recente Doelen</CardTitle>
              <Button variant="link" className="text-[#C2410C]" onClick={() => setActiveTab('doelen')}>
                Alle doelen <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {goals.slice(0, 5).map(goal => {
                  const goalTasks = tasks.filter(t => t.goal_id === goal.id);
                  const completedGoalTasks = goalTasks.filter(t => t.status === 'voltooid').length;
                  const progress = goalTasks.length > 0 
                    ? (completedGoalTasks / goalTasks.length) * 100 
                    : goal.progress;
                  const linkedKPI = getLinkedKPI(goal);

                  return (
                    <div 
                      key={goal.id}
                      className="flex items-center gap-4 p-3 bg-stone-50 rounded-lg cursor-pointer hover:bg-stone-100 transition-colors"
                      onClick={() => openGoalDetail(goal)}
                    >
                      <div 
                        className="w-2 h-12 rounded-full"
                        style={{ backgroundColor: statusColors[goal.status]?.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{goal.title}</p>
                          {linkedKPI && (
                            <Badge className="bg-[#C2410C]/10 text-[#C2410C] text-xs">
                              <Link2 className="w-3 h-3 mr-1" />
                              KPI
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-[#57534E]">
                          <span className="capitalize">{goal.period}</span>
                          <span>{goalTasks.length} taken</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                          {Math.round(progress)}%
                        </p>
                        <Badge className={`${statusColors[goal.status]?.bg} ${statusColors[goal.status]?.text}`}>
                          {statusLabels[goal.status]}
                        </Badge>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#78716C]" />
                    </div>
                  );
                })}
                {goals.length === 0 && (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-[#57534E]">Nog geen doelen. Maak uw eerste doel aan!</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Urgent Tasks */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-rose-500" />
                Urgente Taken
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {tasks.filter(t => t.priority === 'hoog' && t.status !== 'voltooid').slice(0, 5).map(task => (
                  <div 
                    key={task.id}
                    className="flex items-center gap-3 p-3 bg-rose-50 rounded-lg cursor-pointer hover:bg-rose-100"
                    onClick={() => openTaskEdit(task)}
                  >
                    <Checkbox
                      checked={task.status === 'voltooid'}
                      onCheckedChange={(checked) => {
                        handleUpdateTaskStatus(task.id, checked ? 'voltooid' : 'open');
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{task.title}</p>
                      {task.due_date && (
                        <p className="text-sm text-rose-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(task.due_date)}
                        </p>
                      )}
                    </div>
                    <Badge className="bg-rose-100 text-rose-700">Hoog</Badge>
                  </div>
                ))}
                {tasks.filter(t => t.priority === 'hoog' && t.status !== 'voltooid').length === 0 && (
                  <p className="text-center py-4 text-[#57534E]">Geen urgente taken</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Doelen Tab */}
        <TabsContent value="doelen" className="mt-6 space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Periode:</Label>
              <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="maand">Maand</SelectItem>
                  <SelectItem value="kwartaal">Kwartaal</SelectItem>
                  <SelectItem value="jaar">Jaar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm">Status:</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle</SelectItem>
                  <SelectItem value="niet_gestart">Niet gestart</SelectItem>
                  <SelectItem value="bezig">Bezig</SelectItem>
                  <SelectItem value="voltooid">Voltooid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-[#57534E] ml-auto">
              {filteredGoals.length} doelen
            </p>
          </div>

          {/* Goals Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredGoals.length === 0 ? (
              <Card className="col-span-full card-base">
                <CardContent className="py-12 text-center">
                  <Target className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-lg font-semibold">Geen doelen gevonden</p>
                  <p className="text-[#57534E]">Pas filters aan of maak een nieuw doel</p>
                </CardContent>
              </Card>
            ) : (
              filteredGoals.map(goal => {
                const goalTasks = tasks.filter(t => t.goal_id === goal.id);
                const completedGoalTasks = goalTasks.filter(t => t.status === 'voltooid').length;
                const progress = goalTasks.length > 0 
                  ? (completedGoalTasks / goalTasks.length) * 100 
                  : goal.progress;
                const linkedKPI = getLinkedKPI(goal);

                return (
                  <Card 
                    key={goal.id} 
                    className="card-base cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => openGoalDetail(goal)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="text-lg">{goal.title}</CardTitle>
                            {linkedKPI && (
                              <Badge className="bg-[#C2410C]/10 text-[#C2410C] text-xs">
                                <Link2 className="w-3 h-3 mr-1" />
                                KPI
                              </Badge>
                            )}
                            {goal.source === 'kpi' && !linkedKPI && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">
                                📊 KPI Doel
                              </Badge>
                            )}
                            {goal.source === 'marketing_plan' && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                📢 Marketing
                              </Badge>
                            )}
                            {goal.source === 'marketing_plan_main' && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">
                                🎯 Hoofddoel
                              </Badge>
                            )}
                            {goal.source === 'marketing_plan_quarter' && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                📅 Kwartaal
                              </Badge>
                            )}
                            {goal.category && goal.category !== 'kpi' && !goal.source && (
                              <Badge variant="outline" className="text-xs capitalize">
                                {goal.category}
                              </Badge>
                            )}
                          </div>
                          {goal.description && (
                            <p className="text-sm text-[#57534E] mt-1 line-clamp-2">{goal.description}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-center ml-4">
                          <svg className="w-16 h-16" viewBox="0 0 36 36">
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke="#E7E5E4"
                              strokeWidth="3"
                            />
                            <path
                              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                              fill="none"
                              stroke={statusColors[goal.status]?.color || '#C2410C'}
                              strokeWidth="3"
                              strokeDasharray={`${progress}, 100`}
                            />
                            <text x="18" y="20.35" className="text-[8px] font-bold" textAnchor="middle" fill="#1C1917">
                              {Math.round(progress)}%
                            </text>
                          </svg>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3 text-sm text-[#57534E] mb-3 flex-wrap">
                        <Badge className={`${statusColors[goal.status]?.bg} ${statusColors[goal.status]?.text}`}>
                          {statusLabels[goal.status]}
                        </Badge>
                        <span className="capitalize flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {periodLabels[goal.period]}
                        </span>
                        {goal.assigned_to && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {getEmployeeName(goal.assigned_to)}
                          </span>
                        )}
                        {goal.due_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(goal.due_date)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-[#57534E]">
                          {completedGoalTasks}/{goalTasks.length} taken
                        </p>
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            onClick={() => openGoalEdit(goal)}
                          >
                            <Edit className="w-4 h-4 text-[#57534E]" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-8 w-8 p-0 text-rose-600"
                            onClick={() => handleDeleteGoal(goal.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Taken Tab */}
        <TabsContent value="taken" className="mt-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
            <GripVertical className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">
              <strong>Tip:</strong> Pak het ⋮⋮ icoon en sleep de taak naar een andere kolom of naar een doel
            </p>
          </div>
          <DndContext
            sensors={sensors}
            onDragStart={(event) => {
              const task = tasks.find(t => t.id === event.active.id);
              setActiveTask(task);
              setActiveDropId(null);
            }}
            onDragEnd={(event) => {
              const { active, over } = event;
              setActiveTask(null);
              setActiveDropId(null);
              
              if (!over) return;
              
              const taskId = active.id;
              const task = tasks.find(t => t.id === taskId);
              if (!task) return;
              
              const overId = over.id.toString();
              
              // Check if dropped on a goal
              if (overId.startsWith('goal-')) {
                const goalId = overId.replace('goal-', '');
                if (task.goal_id !== goalId) {
                  handleAssignTaskToGoal(taskId, goalId);
                }
                return;
              }
              
              // Check if dropped on "unassign" area
              if (overId === 'unassign') {
                if (task.goal_id) {
                  handleUnassignTask(taskId);
                }
                return;
              }
              
              // Dropped on a column - update status
              const statusMap = {
                'column-open': 'open',
                'column-bezig': 'bezig',
                'column-voltooid': 'voltooid'
              };
              
              const newStatus = statusMap[overId];
              if (newStatus && newStatus !== task.status) {
                handleUpdateTaskStatus(taskId, newStatus);
                toast.success(`Taak verplaatst naar ${newStatus === 'open' ? 'Open' : newStatus === 'bezig' ? 'Bezig' : 'Voltooid'}`);
              }
            }}
            onDragOver={(event) => {
              const { over } = event;
              setActiveDropId(over?.id?.toString() || null);
            }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Status Columns */}
              <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                <DroppableColumn
                  id="column-open"
                  title="Open"
                  tasks={tasks.filter(t => t.status === 'open' || !t.status)}
                  color="bg-stone-400"
                  goals={goals}
                  employees={employees}
                  priorityConfig={priorityConfig}
                  onEdit={openTaskEdit}
                  isOver={activeDropId === 'column-open'}
                />

                <DroppableColumn
                  id="column-bezig"
                  title="Bezig"
                  tasks={tasks.filter(t => t.status === 'bezig')}
                  color="bg-amber-500"
                  goals={goals}
                  employees={employees}
                  priorityConfig={priorityConfig}
                  onEdit={openTaskEdit}
                  isOver={activeDropId === 'column-bezig'}
                />

                <DroppableColumn
                  id="column-voltooid"
                  title="Voltooid"
                  tasks={tasks.filter(t => t.status === 'voltooid')}
                  color="bg-emerald-500"
                  goals={goals}
                  employees={employees}
                  priorityConfig={priorityConfig}
                  onEdit={openTaskEdit}
                  isOver={activeDropId === 'column-voltooid'}
                />
              </div>

              {/* Goals Drop Zone */}
              <Card className="card-base">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#C2410C]" />
                    Sleep naar doel
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                  {/* Unassign drop area */}
                  <DroppableUnassignZone 
                    isOver={activeDropId === 'unassign'} 
                    hasGoal={activeTask?.goal_id}
                  />

                  <div className="border-t my-2" />

                  {/* Goal drop targets */}
                  {goals.filter(g => g.status !== 'voltooid').slice(0, 8).map(goal => (
                    <DroppableGoalTarget
                      key={goal.id}
                      goal={goal}
                      tasks={tasks}
                      isOver={activeDropId === `goal-${goal.id}`}
                      onEdit={openGoalDetail}
                    />
                  ))}

                  {goals.filter(g => g.status !== 'voltooid').length === 0 && (
                    <p className="text-sm text-[#57534E] text-center py-4">
                      Geen actieve doelen
                    </p>
                  )}

                  {goals.filter(g => g.status !== 'voltooid').length > 8 && (
                    <Button 
                      variant="link" 
                      className="w-full text-[#C2410C]"
                      onClick={() => setActiveTab('doelen')}
                    >
                      Meer doelen bekijken...
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            <DragOverlay dropAnimation={null}>
              {activeTask ? (
                <div 
                  className="p-3 bg-white rounded-lg border-2 border-[#C2410C] shadow-2xl cursor-grabbing"
                  style={{ 
                    width: '260px',
                    marginLeft: '-300px',  // Align grip handle center with cursor
                    marginTop: '-22px'     // Align grip handle center with cursor
                  }}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="w-4 h-4 text-[#C2410C] mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{activeTask.title}</p>
                      {activeTask.goal_id && (
                        <p className="text-xs text-[#57534E] mt-1 flex items-center gap-1">
                          <Link2 className="w-3 h-3" />
                          {goals.find(g => g.id === activeTask.goal_id)?.title?.substring(0, 20)}...
                        </p>
                      )}
                    </div>
                    <Badge className={`${priorityConfig[activeTask.priority]?.bg} ${priorityConfig[activeTask.priority]?.color} text-[10px]`}>
                      {activeTask.priority}
                    </Badge>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </TabsContent>
      </Tabs>

      {/* Goal Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl">
          {selectedGoal && (() => {
            const goalTasks = tasks.filter(t => t.goal_id === selectedGoal.id);
            const completedGoalTasks = goalTasks.filter(t => t.status === 'voltooid').length;
            const progress = goalTasks.length > 0 
              ? (completedGoalTasks / goalTasks.length) * 100 
              : selectedGoal.progress;
            const linkedKPI = getLinkedKPI(selectedGoal);

            return (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <DialogTitle className="text-xl flex items-center gap-2 flex-wrap">
                        {selectedGoal.title}
                        {linkedKPI && (
                          <Badge className="bg-[#C2410C]/10 text-[#C2410C]">
                            <Link2 className="w-3 h-3 mr-1" />
                            Gekoppeld aan KPI
                          </Badge>
                        )}
                      </DialogTitle>
                      {selectedGoal.description && (
                        <p className="text-sm text-[#57534E] mt-2">{selectedGoal.description}</p>
                      )}
                    </div>
                    <Badge 
                      className={`ml-4 ${statusColors[selectedGoal.status]?.bg} ${statusColors[selectedGoal.status]?.text}`}
                    >
                      {statusLabels[selectedGoal.status]}
                    </Badge>
                  </div>
                </DialogHeader>
                
                <div className="space-y-6 pt-4">
                  {/* Progress Section */}
                  <div className="p-4 bg-stone-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">Voortgang</span>
                      <span className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <Progress value={progress} className="h-3" />
                    <p className="text-sm text-[#57534E] mt-2">
                      {completedGoalTasks} van {goalTasks.length} taken voltooid
                    </p>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-[#57534E]">Periode</p>
                      <p className="font-medium capitalize">{periodLabels[selectedGoal.period]}</p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-[#57534E]">Categorie</p>
                      <p className="font-medium capitalize">{selectedGoal.category || 'Geen'}</p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-[#57534E]">Verantwoordelijke</p>
                      <p className="font-medium">{selectedGoal.assigned_to ? getEmployeeName(selectedGoal.assigned_to) : 'Niemand'}</p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-[#57534E]">Deadline</p>
                      <p className="font-medium">{selectedGoal.due_date ? formatDate(selectedGoal.due_date) : 'Geen'}</p>
                    </div>
                  </div>

                  {/* Linked KPI Info */}
                  {linkedKPI && (
                    <div className="p-4 bg-[#C2410C]/5 border border-[#C2410C]/20 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#C2410C]">Gekoppeld KPI Doel</p>
                          <p className="text-sm text-[#57534E]">{linkedKPI.name}</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-[#C2410C] border-[#C2410C]"
                          onClick={() => { setDetailDialog(false); navigate('/company/kpi-management'); }}
                        >
                          Bekijk KPI
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Tasks Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold">Taken</h3>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setTaskForm({ ...taskForm, goal_id: selectedGoal.id });
                          setTaskDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Taak toevoegen
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {goalTasks.length === 0 ? (
                        <p className="text-sm text-[#57534E] text-center py-4">Geen taken gekoppeld aan dit doel</p>
                      ) : (
                        goalTasks.map(task => (
                          <div 
                            key={task.id} 
                            className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer"
                            onClick={() => openTaskEdit(task)}
                          >
                            <Checkbox
                              checked={task.status === 'voltooid'}
                              onCheckedChange={(checked) => {
                                handleUpdateTaskStatus(task.id, checked ? 'voltooid' : 'open');
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1">
                              <p className={`font-medium ${task.status === 'voltooid' ? 'line-through text-[#57534E]' : ''}`}>
                                {task.title}
                              </p>
                              {task.assigned_to && (
                                <p className="text-xs text-[#57534E]">{getEmployeeName(task.assigned_to)}</p>
                              )}
                            </div>
                            <Badge className={`${priorityConfig[task.priority]?.bg} ${priorityConfig[task.priority]?.color}`}>
                              {task.priority}
                            </Badge>
                            <Edit className="w-4 h-4 text-[#57534E]" />
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-4 border-t">
                    <Button 
                      className="flex-1"
                      variant="outline"
                      onClick={() => openGoalEdit(selectedGoal)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Bewerken
                    </Button>
                    {selectedGoal.status !== 'voltooid' && (
                      <Button 
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => {
                          handleUpdateGoalStatus(selectedGoal.id, 'voltooid');
                          setDetailDialog(false);
                        }}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Voltooien
                      </Button>
                    )}
                    {selectedGoal.status === 'niet_gestart' && (
                      <Button 
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        onClick={() => {
                          handleUpdateGoalStatus(selectedGoal.id, 'bezig');
                          setDetailDialog(false);
                        }}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start
                      </Button>
                    )}
                    <Button 
                      variant="outline"
                      className="text-rose-600 border-rose-300"
                      onClick={() => handleDeleteGoal(selectedGoal.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoelenStructuurPage;
